import { BehaviorSubject, exhaustMap, Observable, of, timer } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { catchError, debounceTime, delay, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { ApiService } from '../api.service';
import { FileInfo, MalwareScanningStatus } from '../../interfaces';

type FileStatusMonitorRequest = {
  fileId: string;
  projectId: string;
};

type FileStatusMonitor = FileStatusMonitorRequest & {
  dbObject: FileInfo | null;
  updates: BehaviorSubject<FileInfo | null>;
};

const INTERVAL: number = 8000; // 8 seconds interval between polling cycles
const DELAY_BETWEEN_REQUESTS: number = 3000; // 3 seconds delay between requests in a cycle
const DEBOUNCE_INTERVAL: number = 5000; // delay first file to check is polled
const MAX_FILES_TO_POLL: number = 5; // max files to poll in a cycle

@Injectable({
  providedIn: 'root',
})
export class MalwareScanMonitorService {
  private readonly monitorRecord: Map<string, FileStatusMonitor> = new Map<string, FileStatusMonitor>();
  private readonly hasFiles$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private readonly apiService: ApiService = inject(ApiService);

  constructor() {
    this.setupMonitoring();
  }

  public monitorFile(request: FileStatusMonitorRequest): Observable<FileInfo | null> {
    if (!request.fileId) {
      return of(null);
    }

    if (!this.monitorRecord.has(request.fileId)) {
      this.monitorRecord.set(request.fileId, {
        fileId: request.fileId,
        projectId: request.projectId,
        dbObject: null,
        updates: new BehaviorSubject<FileInfo | null>(null),
      });
      this.updateActiveFile();
    }

    return this.monitorRecord.get(request.fileId).updates.asObservable();
  }

  public stopMonitoringFile(fileId: string): void {
    const monitor: FileStatusMonitor | undefined = this.monitorRecord.get(fileId);
    if (!monitor) {
      return;
    }

    monitor.updates.complete();
    this.monitorRecord.delete(fileId);
    this.updateActiveFile();
  }

  private updateActiveFile(): void {
    const hasFiles: boolean = this.monitorRecord.size > 0;
    this.hasFiles$.next(hasFiles);
  }

  private pollProjectFiles(projectId: string, fileIds: string[]): Observable<FileInfo | null> {
    return this.apiService
      .newPost(`/api/projects/${projectId}/storage/poll`, fileIds, {
        uncancelable: true,
      })
      .pipe(
        catchError((err: any): Observable<null> => {
          if (err?.status === 404 && fileIds.length === 1) {
            this.stopMonitoringFile(fileIds[0]);
          }
          return of(null);
        })
      );
  }

  private setupMonitoring(): void {
    this.hasFiles$
      .pipe(
        debounceTime(DEBOUNCE_INTERVAL),
        distinctUntilChanged(),
        switchMap((hasFiles: boolean): Observable<FileInfo[]> => {
          if (!hasFiles) {
            return of([]);
          }

          return timer(0, INTERVAL).pipe(exhaustMap(() => this.executePollingCycle()));
        }),
        tap((results: FileInfo[]) => {
          if (results && results.length > 0) {
            this.processPollResults(results);
          }
        })
      )
      .subscribe();
  }

  private executePollingCycle(): Observable<FileInfo[]> {
    const projectRecord: Record<string, string[]> = this.buildProjectRecord();
    const requests: Observable<FileInfo | null>[] = this.buildPollRequests(projectRecord);

    if (requests.length === 0) {
      return of([]);
    }

    return this.executePollRequests(requests);
  }

  private buildProjectRecord(): Record<string, string[]> {
    const projectRecord: Record<string, string[]> = {};
    this.monitorRecord.forEach((monitor: FileStatusMonitor, fileId: string): void => {
      if (!projectRecord[monitor.projectId]) {
        projectRecord[monitor.projectId] = [];
      }
      projectRecord[monitor.projectId].push(fileId);
    });
    return projectRecord;
  }

  private buildPollRequests(projectRecord: Record<string, string[]>): Observable<FileInfo | null>[] {
    const requests: Observable<FileInfo | null>[] = [];
    Object.entries(projectRecord).forEach(([projectId, fileIds]: [string, string[]]): void => {
      if (fileIds.length === 0) {
        return;
      }

      const fileIdsCopy: string[] = [...fileIds];
      while (fileIdsCopy.length > 0) {
        const batch: string[] = fileIdsCopy.splice(0, MAX_FILES_TO_POLL);
        requests.push(this.pollProjectFiles(projectId, batch));
      }
    });
    return requests;
  }

  private executePollRequests(requests: Observable<FileInfo | null>[]): Observable<FileInfo[]> {
    if (requests.length === 0) {
      return of([]);
    }

    return requests.reduce(
      (acc: Observable<FileInfo[]>, request: Observable<FileInfo | null>, index: number): Observable<FileInfo[]> =>
        acc.pipe(
          delay(index > 0 ? DELAY_BETWEEN_REQUESTS : 0),
          switchMap((previousResults: FileInfo[]) =>
            request.pipe(
              map((result: FileInfo | FileInfo[] | null) => {
                let results: FileInfo[];
                if (Array.isArray(result)) {
                  results = result;
                } else if (result) {
                  results = [result];
                } else {
                  results = [];
                }
                return [...previousResults, ...results];
              })
            )
          )
        ),
      of([])
    );
  }

  private processPollResults(results: FileInfo[]): void {
    results.forEach((dbObject: FileInfo) => {
      if (!dbObject) {
        return;
      }

      const fileId: string = dbObject.id;
      if (!fileId) {
        return;
      }

      if (!dbObject.name) {
        // file not found
        this.stopMonitoringFile(fileId);
        return;
      }

      const monitor: FileStatusMonitor | undefined = this.monitorRecord.get(fileId);
      if (!monitor) {
        return;
      }
      monitor.dbObject = dbObject;
      monitor.updates.next(dbObject);

      const isScanComplete: boolean =
        dbObject.malwareScanningStatus === MalwareScanningStatus.Clean ||
        dbObject.malwareScanningStatus === MalwareScanningStatus.Quarantined;

      if (isScanComplete) {
        this.stopMonitoringFile(fileId);
      }
    });
  }
}
